import React, { forwardRef } from "react";
// The page is always light paper, so it takes the light-ground logo directly
// rather than components/Logo.js, which follows the app's dark mode.
import { logo } from "../../assets";
import { amountInWords, payDate, payPeriod, payslipFigures, rupees } from "./payslipFigures";

/* One salary slip, laid out like an A4 portrait page (styled by the .psd-*
   block in theme.css). Rendered on screen for the viewed month, and off
   screen once per month for the PDF, which captures each as its own page. */

const COMPANY = {
  name: "Rapidsofts",
  address: "2nd floor, Citi Plaza Ambala City, 134003 India",
};

const Fact = ({ label, children }) => (
  <>
    <dt>{label}</dt>
    <dd>
      <span className="psd-colon">:</span>
      {children}
    </dd>
  </>
);

const PayslipDocument = forwardRef(({ payslip }, ref) => {
  const figures = payslipFigures(payslip);
  const period = payPeriod(payslip);
  const name = [payslip?.first_name, payslip?.last_name].filter(Boolean).join(" ");
  const uid = payslip?.uid?.id ?? payslip?.uid;
  const employeeId = payslip?.employee_id || (uid ? `RS-${uid}` : "");
  const rows = Math.max(figures.earnings.length, figures.deductions.length);

  return (
    <div ref={ref} className="psd payslip-document">
      <header className="psd-head">
        <div className="psd-brand">
          <span className="psd-logo">
            <img src={logo} alt="" />
          </span>
          <div>
            <div className="psd-company">{COMPANY.name}</div>
            <div className="psd-muted">{COMPANY.address}</div>
          </div>
        </div>
        <div className="psd-period">
          <div className="psd-muted">Payslip For the Month</div>
          <div className="psd-period-value">{period}</div>
        </div>
      </header>

      <section className="psd-summary">
        <div>
          <div className="psd-caption">Employee Summary</div>
          <dl className="psd-facts">
            <Fact label="Employee Name">{name}</Fact>
            <Fact label="Employee ID">{employeeId}</Fact>
            <Fact label="Pay Period">{period}</Fact>
            <Fact label="Pay Date">{payDate(payslip)}</Fact>
          </dl>
        </div>
        <div className="psd-netcard">
          <div className="psd-netcard-top">
            <div className="psd-net">{rupees(figures.net)}</div>
            <div className="psd-muted">Total Net Pay</div>
          </div>
          <dl className="psd-facts psd-facts-days">
            <Fact label="Paid Days">{figures.paidDays}</Fact>
            <Fact label="LOP Days">{figures.lopDays}</Fact>
          </dl>
        </div>
      </section>

      <table className="psd-table">
        <thead>
          <tr>
            <th>Earnings</th>
            <th className="psd-amount psd-gap">Amount</th>
            <th>Deductions</th>
            <th className="psd-amount">Amount</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => {
            const earning = figures.earnings[i];
            const deduction = figures.deductions[i];
            return (
              <tr key={i}>
                <td>{earning?.label}</td>
                <td className="psd-amount psd-gap">{earning ? rupees(earning.amount) : ""}</td>
                <td>{deduction?.label}</td>
                <td className="psd-amount">{deduction ? rupees(deduction.amount) : ""}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th>Gross Earnings</th>
            <td className="psd-amount psd-gap">{rupees(figures.gross)}</td>
            <th>Total Deductions</th>
            <td className="psd-amount">{rupees(figures.totalDeductions)}</td>
          </tr>
        </tfoot>
      </table>

      <section className="psd-netpay">
        <div>
          <div className="psd-netpay-title">Total Net Payable</div>
          <div className="psd-muted">Gross Earnings - Total Deductions</div>
        </div>
        <div className="psd-netpay-amount">{rupees(figures.net)}</div>
      </section>

      <p className="psd-words">
        <span className="psd-muted">Amount In Words</span> : {amountInWords(figures.net)}
      </p>

      <p className="psd-foot">-- This is a system-generated document. --</p>
    </div>
  );
});

export default PayslipDocument;
